import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../components/Modal/Modal';
import { getBox, openBox } from '../../Requests/boxes/boxes';
import type { BoxExact, Reward as RewardType } from '../../types/Boxes/boxes';
import styles from './BoxModal.module.css';
import Loader from '../../components/Loader/Loader';
import Reward from '../../components/Reward/Reward';
import boxbg from '../../assets/backgroundBox.jpg';
import { ReactComponent as ClockIcon } from '../../assets/clock.svg';
import { ReactComponent as CarretRightBlue } from '../../assets/CaretRightBlue.svg';
import tickSound from '../../assets/tickk.mp3';
import BoxModalInstructionItem from './BoxModalInstructionItem';
import cpcIcon from '../../assets/tasksIcons/cpcIconNew.png';
import { useBluePageStore } from '../../store/BluePageStore';
import { useModalStore } from '../../store/ModalStore';
import bluePageImage from '../../assets/mockImg.png';
const BoxModal = ({ chosenId }: { chosenId: number }) => {
  const [box, setBox] = useState<BoxExact | null>(null);
  const [loading, setLoading] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  // masks removed
  const [isSpinning, setIsSpinning] = useState(false);
  const [prizeToShow, setPrizeToShow] = useState<RewardType | null>(null);
  // NEW: remember position before spin so we can restore it
  const [preSpinOffset, setPreSpinOffset] = useState<number | null>(null);
  // Haptic feedback state
  const [lastCarouselPosition, setLastCarouselPosition] = useState<
    number | null
  >(null);
  const [audioPool, setAudioPool] = useState<HTMLAudioElement[]>([]);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [canCloseModal, setCanCloseModal] = useState(true);
  const setOpen = useBluePageStore((state) => state.setOpen);
  const setTitle = useBluePageStore((state) => state.setTitle);
  const setDescription = useBluePageStore((state) => state.setDescription);
  const setRewards = useBluePageStore((state) => state.setRewards);
  const setOnClose = useBluePageStore((state) => state.setOnClose);
  const setOnClaimOnBoxModal = useBluePageStore(
    (state) => state.setOnClaimOnBoxModal,
  );
  const setModalOpen = useModalStore((state) => state.setClosing);

  // mask states removed
  useEffect(() => {
    if (chosenId === 0) {
      return;
    }
    const getBoxExact = async () => {
      setLoading(true);
      const response = await getBox(chosenId);
      console.log(response, 'responseBox');
      setBox(response);
      setLoading(false);
    };
    getBoxExact();
  }, [chosenId]);

  const handleSpinBox = async () => {
    const res = await openBox(chosenId);
    console.log(res, 'res');
    const prizeId = res.reward.id;
    if (typeof prizeId === 'number') {
      spinToPrizeById(prizeId);
    }
  };

  const DUPLICATION_TIMES = 4;
  const ITEM_WIDTH = 125;
  const GAP = 8;

  const SPIN_WAAPI_SETTLE_SECONDS = 0.5; // фаза досадки к цели
  const SPIN_WAAPI_DURATIONS = [5, 6, 7, 8, 9, 10];

  // Перемешать массив
  const shuffle = <T,>(arr: T[]): T[] => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  // Подготовка ленты в состояние: перемешиваем и дублируем
  const [carouselRewards, setCarouselRewards] = useState<RewardType[]>([]);
  const generateCarouselRewards = useCallback(
    (base: RewardType[] | undefined, times = DUPLICATION_TIMES) => {
      const rewards = base ?? [];
      if (!rewards.length) return [] as RewardType[];
      const shuffled = shuffle(rewards);
      const dupTimes = Math.max(3, Math.min(5, times));
      const out: RewardType[] = [];
      for (let t = 0; t < dupTimes; t += 1) out.push(...shuffle(shuffled));
      // debug: регенерация ленты призов
      console.log('[BoxModal] Регенерация карусели:', {
        baseRewards: rewards.length,
        duplicationTimes: dupTimes,
        resultLength: out.length,
      });
      return out;
    },
    [],
  );
  useEffect(() => {
    setCarouselRewards(generateCarouselRewards(box?.rewards));
  }, [box?.rewards, generateCarouselRewards]);

  useEffect(() => {
    const pool: HTMLAudioElement[] = [];
    const poolSize = 5;

    for (let i = 0; i < poolSize; i++) {
      const audio = new Audio(tickSound);
      audio.preload = 'auto';
      audio.volume = 1;
      pool.push(audio);
    }

    setAudioPool(pool);

    return () => {
      pool.forEach((audio) => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  // Функция для воспроизведения звука из пула
  const playTickSound = useCallback(() => {
    if (audioPool.length === 0) return;

    const audio = audioPool[currentAudioIndex];
    audio.currentTime = 0; // Сбрасываем на начало
    audio.play().catch(console.error);

    // Переходим к следующему Audio объекту в пуле
    setCurrentAudioIndex((prev) => (prev + 1) % audioPool.length);
  }, [audioPool, currentAudioIndex]);

  useEffect(() => {
    if (!isSpinning || !trackRef.current) return;

    const tg = window.Telegram?.WebApp;
    if (!tg?.HapticFeedback) return;

    const checkCarouselPosition = () => {
      if (!trackRef.current) return;

      const style = getComputedStyle(trackRef.current);
      const matrix = new DOMMatrixReadOnly(style.transform);
      const currentPosition = matrix.m41;

      if (lastCarouselPosition !== null) {
        const positionChange = Math.abs(currentPosition - lastCarouselPosition);
        if (positionChange >= 127) {
          tg.HapticFeedback.impactOccurred('soft');

          // Воспроизводим звук
          playTickSound();

          setLastCarouselPosition(currentPosition);
        }
      } else {
        // Первая проверка - устанавливаем начальную позицию
        setLastCarouselPosition(currentPosition);
      }
    };

    const interval = setInterval(checkCarouselPosition, 50);

    return () => clearInterval(interval);
  }, [isSpinning, lastCarouselPosition, playTickSound]);

  const initialCenterIndex = useMemo(() => {
    if (carouselRewards.length === 0) return 0;
    return Math.min(5, carouselRewards.length - 1);
  }, [carouselRewards.length]);

  const computeOffsetToCenter = (index: number) => {
    const viewportWidth = viewportRef.current?.offsetWidth ?? 0;
    const itemFullWidth = ITEM_WIDTH + GAP;
    const targetCenter = index * itemFullWidth + ITEM_WIDTH / 2;
    const viewportCenter = viewportWidth / 2;
    const raw = viewportCenter - targetCenter;
    return raw;
  };

  useEffect(() => {
    if (!trackRef.current) return;
    const offset = computeOffsetToCenter(initialCenterIndex);
    const el = trackRef.current;
    const prev = el.style.transition;
    el.style.transition = 'none';
    el.style.transform = `translateX(${offset}px)`;
    // force reflow, then restore transition for будущие анимации
    void el.offsetHeight;
    el.style.transition = prev || '';
  }, [initialCenterIndex, carouselRewards.length]);

  const spinToPrizeById = (prizeId: number) => {
    if (!trackRef.current) return;
    const style = getComputedStyle(trackRef.current);
    const matrix = new DOMMatrixReadOnly(style.transform);
    const currentTranslateX = matrix.m41;
    const viewportWidth = viewportRef.current?.offsetWidth ?? 0;
    const itemFull = ITEM_WIDTH + GAP;
    const currentCenterPos = viewportWidth / 2 - currentTranslateX;
    const approxIndex = Math.round(
      (currentCenterPos - ITEM_WIDTH / 2) / itemFull,
    );

    const ensureMinDistance = (
      base: RewardType[],
      minAhead: number,
    ): { list: RewardType[]; index: number } => {
      let list = base.slice();
      let desiredIndex = -1;
      const minIndex = Math.max(approxIndex + minAhead, 0);
      const findFrom = (arr: RewardType[], from: number) => {
        for (let i = from; i < arr.length; i += 1)
          if (arr[i].id === prizeId) return i;
        return -1;
      };
      desiredIndex = findFrom(list, minIndex);
      while (desiredIndex === -1) {
        list = list.concat(generateCarouselRewards(box?.rewards));
        desiredIndex = findFrom(list, minIndex);
        if (list.length > 2000) break; // предохранитель
      }
      return { list, index: desiredIndex === -1 ? minIndex : desiredIndex };
    };

    const { list: prepared, index: candidateIndex } = ensureMinDistance(
      carouselRewards.length
        ? carouselRewards
        : generateCarouselRewards(box?.rewards),
      20,
    );

    let adjusted = prepared;
    const minTail = 8;
    if (adjusted.length - 1 - candidateIndex < minTail) {
      const need = minTail - (adjusted.length - 1 - candidateIndex);
      const baseLen = box?.rewards?.length || 1;
      const blocks = Math.ceil(need / baseLen) + 1;
      for (let b = 0; b < blocks; b += 1) {
        adjusted = adjusted.concat(generateCarouselRewards(box?.rewards));
      }
    }

    const needsUpdate = adjusted.length !== carouselRewards.length;
    if (needsUpdate) setCarouselRewards(adjusted);

    const proceedSpin = () => {
      const el = trackRef.current;
      if (!el) return;
      setIsSpinning(true);
      setCanCloseModal(false);
      setLastCarouselPosition(null);

      // NEW: remember pre-spin position to restore later
      const style2 = getComputedStyle(el);
      const matrix2 = new DOMMatrixReadOnly(style2.transform);
      setPreSpinOffset(matrix2.m41);
      const targetOffset = computeOffsetToCenter(candidateIndex);
      const style = getComputedStyle(el);
      const matrix = new DOMMatrixReadOnly(style.transform);
      const currentX = matrix.m41;
      type WithAnimations = Element & { getAnimations?: () => Animation[] };
      const nodeWithAnims = el as unknown as WithAnimations;
      if (typeof nodeWithAnims.getAnimations === 'function') {
        nodeWithAnims.getAnimations!().forEach((a) => a.cancel());
      }
      type Animator = Element & {
        animate: (
          keyframes: Keyframe[] | PropertyIndexedKeyframes,
          options?: number | KeyframeAnimationOptions,
        ) => Animation;
      };
      const animator = el as unknown as Animator;
      const overshoot = targetOffset - 24;

      const spinMainSeconds =
        SPIN_WAAPI_DURATIONS[
          Math.floor(Math.random() * SPIN_WAAPI_DURATIONS.length)
        ];

      const anim1 = animator.animate(
        [
          { transform: `translateX(${currentX}px)` },
          { transform: `translateX(${overshoot}px)` },
        ],
        {
          duration: Math.max(300, Math.round(spinMainSeconds * 1000)),
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards',
        },
      );
      anim1.onfinish = () => {
        const anim2 = animator.animate(
          [
            { transform: `translateX(${overshoot}px)` },
            { transform: `translateX(${targetOffset}px)` },
          ],
          {
            duration: Math.max(
              200,
              Math.round(SPIN_WAAPI_SETTLE_SECONDS * 1000),
            ),
            easing: 'ease-out',
            fill: 'forwards',
          },
        );
        anim2.onfinish = () => {
          el.style.transform = `translateX(${targetOffset}px)`;
          setIsSpinning(false);
          const rewards = box?.rewards ?? [];
          const won = rewards.find((r) => r.id === prizeId) || null;
          setPrizeToShow(won);
          console.log('won', won);
          setOpen(true);
          setModalOpen(true);
          setTitle(
            `Ты выиграл подарок стоимостью ${won?.quantity} ${won?.currency}`,
          );
          setDescription('Он будет доступен в твоем инвентаре');
          setRewards([won]);
          setOnClose(() => {
            setPrizeToShow(null);
            console.log('onClose');
            requestAnimationFrame(() => {
              const el = trackRef.current;
              if (!el) return;
              el.getAnimations?.().forEach((a) => a.cancel());
              el.style.transition = 'none';
              const restore = preSpinOffset ?? 0;
              el.style.transform = `translateX(${restore}px)`;
              void el.offsetHeight;
            });
          });
          setOnClaimOnBoxModal(() => setCanCloseModal(true));

          const tg = window.Telegram?.WebApp;
          if (tg?.HapticFeedback) {
            console.log('Triggering final haptic feedback');
            tg.HapticFeedback.notificationOccurred('success');

            playTickSound();
          }
        };
        anim2.oncancel = () => {
          setIsSpinning(false);
        };
      };
      anim1.oncancel = () => {
        setIsSpinning(false);
      };
    };

    if (needsUpdate) setTimeout(proceedSpin, 0);
    else proceedSpin();
  };
  const isCompleted =
    box?.related_task?.user_metadata.progress ===
    box?.related_task?.target_completion_count;
  console.log(isCompleted, 'isCompleted');
  return (
    <Modal noTopPadding={!loading} noCloseButton={!canCloseModal}>
      <div className={styles.boxModal}>
        {loading ? (
          <Loader />
        ) : box?.obtain_method === 'task' && !isCompleted ? (
          <div className={styles.container}>
            <h2 className={styles.boxModalTitle}>{box?.name}</h2>
            <div className={styles.boxBigImage}>
              <div
                className={styles.boxBigImageInner}
                style={{ backgroundImage: `url(${boxbg})` }}
              >
                <div className={styles.ribbonBig}>
                  <span className={styles.ribbonInnerBig}>
                    <span className={styles.ribbonTextBig}>Эксклюзив</span>
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.boxInstructions}>
              <div className={styles.boxInstructionsTitle}>
                Выполни задания, чтобы открыть бокс
              </div>
              <div className={styles.instructionList}>
                <>
                  <BoxModalInstructionItem
                    key={box?.related_task?.id}
                    task={box?.related_task}
                    progress={box?.related_task?.user_metadata.progress}
                    icon={<img src={cpcIcon} alt="CPC" />}
                  />
                  <BoxModalInstructionItem
                    key={box?.related_task?.id}
                    task={box?.related_task}
                    progress={box?.related_task?.user_metadata.progress}
                    icon={<img src={cpcIcon} alt="CPC" />}
                  />
                  <BoxModalInstructionItem
                    key={box?.related_task?.id}
                    task={box?.related_task}
                    progress={box?.related_task?.user_metadata.progress}
                    icon={<img src={cpcIcon} alt="CPC" />}
                  />
                </>
              </div>
            </div>
            <div className={styles.buttonContainer}>
              {!isCompleted ? (
                <button className={styles.buttonInactive}>
                  Выполнено 0/1 заданий
                </button>
              ) : (
                <button className={styles.button} onClick={handleSpinBox}>
                  Открыть бокс
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.boxModalTitle}>{box?.name}</div>
            <div className={`${styles.carousel} ${styles.container}`}>
              <div className={styles.carouselGuideLineTop} />
              <div className={styles.carouselSpacer12} />
              <div className={styles.carouselViewport} ref={viewportRef}>
                <div className={styles.carouselTrack} ref={trackRef}>
                  {carouselRewards.map((reward, index) => (
                    <div className={styles.carouselItem} key={`cr_${index}`}>
                      <div
                        className={styles.carouselBox}
                        style={{ backgroundImage: `url(${boxbg})` }}
                      >
                        <div className={styles.carouselBoxImage}>
                          <img src={bluePageImage} alt="carousel image" />
                        </div>
                        <div className={styles.carouselBoxPrice}>
                          <Reward
                            reward={reward.quantity}
                            size="large"
                            type="star"
                          />
                        </div>
                        <div className={styles.ribbon}>
                          <span className={styles.ribbonInner}>
                            <span className={styles.ribbonText}>
                              {reward.winrate}%
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.carouselSpacer12} />
              <div className={styles.carouselGuideLineBottom} />
            </div>
            <div
              className={styles.winnersToGo}
              onClick={() => {
                const total = box?.winners?.length || 0;
                if (!total) return;
                const middleIndex = Math.floor((total - 1) / 2);
                const items = document.querySelectorAll(
                  `.${styles.winnerListItem}`,
                );
                const target = items[middleIndex] as HTMLElement | undefined;
                target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              <ClockIcon className={styles.winnersToGoIcon} />
              <div className={styles.winnersToGoTitle}>История победителей</div>
              <div>
                <CarretRightBlue />
              </div>
            </div>
            <div className={styles.prizes}>
              <div className={styles.prizesTitle}>Возможные призы</div>
              <div className={styles.prizesList}>
                {box?.rewards.map((reward, index) => (
                  <div className={styles.prize}>
                    <div
                      key={index}
                      className={styles.collectionBox}
                      // style={{ backgroundImage: `url(${box.box_image})` }}
                      style={{ backgroundImage: `url(${boxbg})` }}
                    >
                      {/* <img src={box.box_image} alt={box.name} /> */}
                      <div className={styles.collectionBoxPrice}>
                        <Reward reward={reward.quantity} size="large" />
                      </div>
                      <div className={styles.ribbon}>
                        <span className={styles.ribbonInner}>
                          <span className={styles.ribbonText}>
                            {reward.winrate}%
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.winnerList}>
              <div className={styles.winnerListTitle}>История победителей</div>
              <div className={styles.winnerListItems}>
                {box?.winners.map((winner, index) => (
                  <div className={styles.winnerListItem} key={index}>
                    <div className={styles.winnerListItemImage}>
                      <img src={boxbg} alt="winner avatar" />
                    </div>
                    <div className={styles.winnerListItemInfo}>
                      <div className={styles.winnerListItemInfoTop}>
                        <div className={styles.winnerListItemInfoTopName}>
                          {winner.user.name} {winner.user.name}{' '}
                          {winner.user.name} {winner.user.name}
                        </div>
                        <div className={styles.winnerListItemInfoTopTime}>
                          3 минуты назад
                        </div>
                      </div>
                      <div className={styles.winnerListItemInfoBottom}>
                        <div className={styles.winnerListItemInfoBottomPrice}>
                          Выиграл подарок за{' '}
                          <span
                            className={
                              styles.winnerListItemInfoBottomPricePrice
                            }
                          >
                            {winner.prize_price}{' '}
                          </span>
                          {winner.prize_currency}
                        </div>
                      </div>
                    </div>
                    <div className={styles.winnerListItemInfoPrizeItself}>
                      <img src={boxbg} alt="prize" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <button
                className={styles.button}
                disabled={isSpinning}
                onClick={handleSpinBox}
              >
                {isSpinning ? 'Открываем…' : 'Открыть бокс'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BoxModal;
